/**
 * Admin V3 — Bookings module — Timeline modal (read-only)
 *
 * Renders booking.status_timeline already returned by GET /admin/bookings.
 * Zero endpoint calls; pure presentation.
 *
 * IMPROVEMENT OVER M2A:
 * Production status_timeline entries come in two shapes (drift discovered
 * during Phase 2.2 audit):
 *   Shape A — { event_type, timestamp, details }  (booking creation, webhooks, refund)
 *   Shape B — { event,      timestamp, data    }  (atomic primitives: assign/unassign)
 * M2A's modal only handled Shape B. This V3 modal handles BOTH defensively.
 *
 * AdminDashboard.js (legacy) is NOT imported. timelineEventLabels and
 * SectionCard come from V3's own visualTokens.js (verbatim port from legacy).
 */

import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Clock, ChevronRight, ChevronDown } from 'lucide-react';
import { cn, timelineEventLabels, TierBadge } from '../../shared/visualTokens';
import { t } from '../../i18n';

const formatTs = (iso, locale) => {
  if (!iso) return '';
  try {
    const d = typeof iso === 'string' ? new Date(iso) : iso;
    return new Intl.DateTimeFormat(locale === 'fr' ? 'fr-FR' : 'fr-FR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    }).format(d);
  } catch {
    return String(iso);
  }
};

// Defensive extractor handling both Shape A and Shape B.
const extractEvent = (ev) => {
  const eventKey = ev?.event || ev?.event_type || null;
  const payload  = ev?.metadata || ev?.data || ev?.details || null;
  const actor =
    ev?.actor ||
    payload?.actor ||
    payload?.assigned_by ||
    payload?.unassigned_by ||
    payload?.cancelled_by ||
    payload?.admin_id ||
    null;
  const actorType = ev?.actor_type || payload?.actor_type || null;
  const ts = ev?.timestamp || null;
  return { eventKey, payload, actor, actorType, ts };
};

const BookingTimelineModal = ({ open, onClose, booking, locale = 'fr' }) => {
  // Per-event expand state for the technical payload toggle (Phase 2.2 UX polish).
  // Reset implicitly when the modal closes (component remounts on next open).
  const [expandedPayloads, setExpandedPayloads] = useState({});

  if (!booking) return null;

  const togglePayload = (idx) => {
    setExpandedPayloads((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  const events = Array.isArray(booking.status_timeline) ? booking.status_timeline : [];
  const subtitle = t('bookings.timeline.subtitle', locale).replace(
    '{ref}',
    booking.booking_reference || booking.id?.slice(0, 8) || ''
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white border-slate-200 max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-slate-900">{t('bookings.timeline.title', locale)}</DialogTitle>
          <DialogDescription className="text-slate-500 flex items-center gap-2">
            <span>{subtitle}</span>
            {/*
              SC-PR-7: show the customer-widget tier badge in the modal
              header (right next to the booking reference subtitle) so the
              operator opening the timeline sees at a glance which tier the
              customer selected. Renders nothing for legacy bookings whose
              service_type is null (TierBadge handles fallback internally).
            */}
            {booking.service_type ? <TierBadge tier={booking.service_type} /> : null}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 pt-4 max-h-[60vh] overflow-y-auto">
          {events.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <Clock className="h-10 w-10 text-slate-300 mb-3" />
              <p className="text-sm font-medium text-slate-500">{t('bookings.timeline.empty', locale)}</p>
            </div>
          ) : (
            events.map((ev, idx) => {
              const { eventKey, payload, actor, actorType, ts } = extractEvent(ev);
              const meta = (eventKey && timelineEventLabels[eventKey]) || {
                label: eventKey || '—',
                dot: 'bg-slate-400',
                color: 'text-slate-700',
              };
              return (
                <div key={idx} className="rounded-xl border border-slate-200 bg-slate-50/60 p-3">
                  <div className="flex items-start gap-3">
                    <div className={cn('mt-1 h-2.5 w-2.5 rounded-full flex-shrink-0', meta.dot)} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className={cn('text-sm font-semibold', meta.color)}>{meta.label}</span>
                        <span className="text-[11px] text-slate-400 whitespace-nowrap">{formatTs(ts, locale)}</span>
                      </div>
                      {(actor || actorType) && (
                        <div className="text-[12px] text-slate-500 mt-0.5">
                          {t('bookings.timeline.actor.by', locale)} {actor || '—'}
                          {actorType ? ` (${actorType})` : ''}
                        </div>
                      )}
                      {payload && typeof payload === 'object' && Object.keys(payload).length > 0 && (
                        <div className="mt-1.5">
                          <button
                            type="button"
                            onClick={() => togglePayload(idx)}
                            className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-400 hover:text-slate-700 transition-colors"
                            data-testid={`adminv3-timeline-payload-toggle-${idx}`}
                          >
                            {expandedPayloads[idx]
                              ? <ChevronDown className="h-3 w-3" />
                              : <ChevronRight className="h-3 w-3" />}
                            {expandedPayloads[idx]
                              ? t('bookings.timeline.payload.hide', locale)
                              : t('bookings.timeline.payload.show', locale)}
                          </button>
                          {expandedPayloads[idx] && (
                            <pre className="mt-2 rounded-lg bg-white border border-slate-200 p-2 text-[11px] font-mono text-slate-600 overflow-x-auto">
{JSON.stringify(payload, null, 2)}
                            </pre>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <DialogFooter className="mt-4">
          <Button
            variant="outline"
            onClick={onClose}
            className="rounded-xl border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
          >
            {t('bookings.timeline.close', locale)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BookingTimelineModal;
